import type { TimeseriesCsvExportRequest } from '@highimpact/react-hooks';
import type {
  CurveId,
  CurvePreferences,
  ExportCsvFormValues,
  ExportCsvTimeRanges,
} from '@highimpact/scatterplot';

// -- type definitions --

/** Input needed to turn the export form into a transport CSV request. */
export interface BuildExportCsvTransportRequestInput {
  formValues: ExportCsvFormValues;
  curvePrefs: Record<CurveId, CurvePreferences>;
  curveToSourceIndex: Record<CurveId, number>;
  timeRanges: ExportCsvTimeRanges;
}

// -- helper functions --

/** Pick the time span chosen in the export form. */
function resolveExportTimeRange(
  formValues: ExportCsvFormValues,
  timeRanges: ExportCsvTimeRanges
): TimeseriesCsvExportRequest['timeRange'] | undefined {
  const selectedRange =
    formValues.timeRangeChoice === 'visible'
      ? timeRanges.visible
      : timeRanges.loaded;

  if (!selectedRange) {
    return undefined;
  }

  return {
    start: new Date(selectedRange[0]).toISOString(),
    end: new Date(selectedRange[1]).toISOString(),
  };
}

/** Build the curve labels used as CSV column names. */
function buildCurveDisplayNames(
  selectedCurveIds: readonly CurveId[],
  curvePrefs: Record<CurveId, CurvePreferences>
): Record<string, string> {
  return Object.fromEntries(
    selectedCurveIds.map((curveId) => [
      curveId,
      curvePrefs[curveId]?.displayName ?? curveId,
    ])
  );
}

// -- main export --

/** Map export dialog values to a transport CSV request. */
export function buildExportCsvTransportRequest(
  input: BuildExportCsvTransportRequestInput
): TimeseriesCsvExportRequest {
  const { formValues, curvePrefs, curveToSourceIndex, timeRanges } = input;

  return {
    timeRange: resolveExportTimeRange(formValues, timeRanges),
    decimation: formValues.decimation.enabled
      ? { enabled: true, points: formValues.decimation.points }
      : { enabled: false },
    selectedCurveIds: formValues.selectedCurveIds,
    curveDisplayNames: buildCurveDisplayNames(
      formValues.selectedCurveIds,
      curvePrefs
    ),
    curveToSourceIndex,
  };
}
